import { Button } from '@fluentui/react-button';
import { Field } from '@fluentui/react-field';
import { makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import { Input, InputOnChangeData } from '@fluentui/react-input';
import { useEffect, useState } from 'react';
import { useChat } from '../../../libs/hooks';
import { AlertType } from '../../../libs/models/AlertType';
import { useAppDispatch, useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import { addAlert } from '../../../redux/features/app/appSlice';
import { editConversationTitle } from '../../../redux/features/conversations/conversationsSlice';
import { Breakpoints } from '../../../styles';
import { Checkmark20, Dismiss20 } from '../../shared/BundledIcons';
import { getErrorDetails } from '../../utils/TextUtils';

const useClasses = makeStyles({
    root: {
        width: '100%',
        ...Breakpoints.small({
            display: 'none',
        }),
    },
    fieldContainer: {
        width: '100%',
        marginBottom: tokens.spacingVerticalS,
    },
    buttons: {
        display: 'flex',
        alignSelf: 'end',
    },
    textButtons: {
        ...shorthands.gap(tokens.spacingVerticalS),
    },
    rootEditingWithValidation: {
        height: '60px',
    },
});

interface IEditChatNameProps {
    name: string;
    chatId: string;
    exitEdits: () => void;
    textButtons?: boolean;
    onValidationChange?: (hasError: boolean) => void;
}

export const EditChatName: React.FC<IEditChatNameProps> = ({ name, chatId, exitEdits, textButtons, onValidationChange }) => {
    const classes = useClasses();
    const dispatch = useAppDispatch();
    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const chat = useChat();

    const [title = '', setTitle] = useState<string | undefined>(name);
    const [validationError, setValidationError] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (selectedId !== chatId) exitEdits();
    }, [chatId, exitEdits, selectedId]);

    const onSaveTitleChange = async () => {
        setValidationError(undefined);

        const trimmedTitle = title.trim();
        if (trimmedTitle.length === 0) {
            setValidationError('Der Chat-Name darf nicht leer sein.');
            return;
        }

        if (name !== trimmedTitle) {
            const chatState = conversations[selectedId];
            await chat.editChat(chatId, trimmedTitle, chatState.systemDescription, chatState.memoryBalance).then(() => {
                dispatch(editConversationTitle({ id: chatId, newTitle: trimmedTitle }));
            });
        }
        exitEdits();
    };

    const onClose = () => {
        setTitle(name);
        setValidationError(undefined);
        exitEdits();
    };

    const onTitleChange = (_ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
        setTitle(data.value);
    };

    const handleSave = () => {
        onSaveTitleChange().catch((e: any) => {
            const errorMessage = `Unable to retrieve chat to change title. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        });
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLElement> = (event) => {
        if (event.key === 'Enter') {
            handleSave();
        }
    };

    useEffect(() => {
        if (onValidationChange) {
            onValidationChange(!!validationError);
        }
    }, [validationError, onValidationChange]);

    return (
        <div
            className={classes.root}
            style={{
                display: 'flex',
                flexDirection: textButtons ? 'column' : 'row',
                gap: textButtons ? tokens.spacingVerticalS : tokens.spacingVerticalNone,
            }}
            title={'Chat-Name bearbeiten'}
            aria-label={`Edit chat name for "${name}"`}
        >
            <div>
                <Field
                    required
                    validationState={validationError ? 'error' : 'none'}
                    validationMessage={validationError}
                    className={classes.fieldContainer}
                >
                    <Input
                        value={title}
                        onChange={onTitleChange}
                        id={`input-${chatId}`}
                        onKeyDown={handleKeyDown}
                        autoFocus
                    />
                </Field>
            </div>
            {textButtons && (
                <div className={mergeClasses(classes.buttons, classes.textButtons)}>
                    <Button appearance="secondary" onClick={onClose}>
                        Abbrechen
                    </Button>
                    <Button type="submit" appearance="primary" onClick={handleSave}>
                        Speichern
                    </Button>
                </div>
            )}
            {!textButtons && (
                <div className={classes.buttons}>
                    <Button
                        icon={<Dismiss20 />}
                        onClick={onClose}
                        appearance="transparent"
                    />
                    <Button
                        icon={<Checkmark20 />}
                        onClick={handleSave}
                        appearance="transparent"
                    />
                </div>
            )}
        </div>
    );
};
